import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  Pagination,
  Spinner,
  Alert,
  InputGroup,
  Modal,
} from 'react-bootstrap';
import {
  Article,
  ArticleCategory,
  ArticleTag,
  ArticleSearchParams,
} from '../models/article';
import articleService from '../services/articleService';
import AdminMenuButton from '../components/common/AdminMenuButton';
import SideMenu from '../components/common/SideMenu';

const Articles: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [tags, setTags] = useState<ArticleTag[]>([]);
  const [totalArticles, setTotalArticles] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Search state
  const [searchKeyword, setSearchKeyword] = useState(
    searchParams.get('keyword') || ''
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    searchParams.get('categories')?.split(',') || []
  );
  const [selectedTags, setSelectedTags] = useState<string[]>(
    searchParams.get('tags')?.split(',') || []
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1')
  );
  const [sortBy, setSortBy] = useState<ArticleSearchParams['sortBy']>(
    (searchParams.get('sortBy') as ArticleSearchParams['sortBy']) ||
      'created_at'
  );
  const [sortOrder, setSortOrder] = useState<ArticleSearchParams['sortOrder']>(
    (searchParams.get('sortOrder') as ArticleSearchParams['sortOrder']) ||
      'desc'
  );

  const itemsPerPage = 10;

  // 記事データの取得
  const loadArticles = async (params?: ArticleSearchParams) => {
    setLoading(true);
    setError(null);

    try {
      const searchParamsToUse: ArticleSearchParams = {
        keyword: searchKeyword || undefined,
        categories:
          selectedCategories.length > 0 ? selectedCategories : undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        ...params,
      };

      const [articlesData, categoriesData, tagsData] = await Promise.all([
        articleService.getArticles(searchParamsToUse),
        articleService.getCategories(),
        articleService.getTags(),
      ]);

      setArticles(articlesData.articles);
      setTotalArticles(articlesData.total);
      setCategories(categoriesData);
      setTags(tagsData);

      // URLパラメータを更新
      updateSearchParams(searchParamsToUse);
    } catch (err) {
      setError('記事の取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // URLパラメータの更新
  const updateSearchParams = (params: ArticleSearchParams) => {
    const newSearchParams = new URLSearchParams();

    if (params.keyword) newSearchParams.set('keyword', params.keyword);
    if (params.categories?.length)
      newSearchParams.set('categories', params.categories.join(','));
    if (params.tags?.length) newSearchParams.set('tags', params.tags.join(','));
    if (params.page && params.page > 1)
      newSearchParams.set('page', params.page.toString());
    if (params.sortBy && params.sortBy !== 'created_at')
      newSearchParams.set('sortBy', params.sortBy);
    if (params.sortOrder && params.sortOrder !== 'desc')
      newSearchParams.set('sortOrder', params.sortOrder);

    setSearchParams(newSearchParams);
  };

  // 初回データ読み込み
  useEffect(() => {
    loadArticles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 検索実行
  const handleSearch = () => {
    setCurrentPage(1);
    loadArticles({ page: 1 });
  };

  // ページング
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadArticles({ page });
  };

  // ソート変更
  const handleSortChange = (
    newSortBy: ArticleSearchParams['sortBy'],
    newSortOrder: ArticleSearchParams['sortOrder']
  ) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
    loadArticles({ sortBy: newSortBy, sortOrder: newSortOrder, page: 1 });
  };

  // 記事詳細表示
  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    setShowModal(true);
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedArticle(null);
  };

  // 日付フォーマット
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // HTMLタグを除去してプレーンテキストに変換
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // カテゴリフィルター
  const handleCategoryFilter = (categoryName: string) => {
    const newCategories = selectedCategories.includes(categoryName)
      ? selectedCategories.filter(c => c !== categoryName)
      : [...selectedCategories, categoryName];

    setSelectedCategories(newCategories);
    setCurrentPage(1);
    loadArticles({
      categories: newCategories.length > 0 ? newCategories : undefined,
      page: 1,
    });
  };

  // タグフィルター
  const handleTagFilter = (tagName: string) => {
    const newTags = selectedTags.includes(tagName)
      ? selectedTags.filter(t => t !== tagName)
      : [...selectedTags, tagName];

    setSelectedTags(newTags);
    setCurrentPage(1);
    loadArticles({ tags: newTags.length > 0 ? newTags : undefined, page: 1 });
  };

  // 記事削除
  const handleDeleteArticle = async (articleId: number) => {
    if (!window.confirm('この記事を削除しますか？')) return;

    try {
      await articleService.deleteArticle(articleId);
      loadArticles(); // 一覧を再読み込み
    } catch (err) {
      setError('記事の削除に失敗しました');
      console.error(err);
    }
  };

  // フィルターのクリア
  const clearFilters = () => {
    setSearchKeyword('');
    setSelectedCategories([]);
    setSelectedTags([]);
    setCurrentPage(1);
    setSortBy('created_at');
    setSortOrder('desc');
    loadArticles({
      keyword: undefined,
      categories: undefined,
      tags: undefined,
      page: 1,
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
  };

  // ページネーション計算
  const totalPages = Math.ceil(totalArticles / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalArticles);

  // シンプルな画像URL構築（フック使用を最小限に）
  const getImageUrl = (photoPath: string | null): string | null => {
    if (!photoPath) return null;

    if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
      return photoPath;
    }

    const baseUrl =
      process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const cleanPath = photoPath.replace(/^\/+/, '');
    return `${baseUrl}/${cleanPath}`;
  };

  return (
    <Container fluid>
      <Row className="min-vh-100 g-0">
        <Col xs={12} md={2}>
          <SideMenu />
        </Col>

        <Col xs={12} md={10}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="text-warning white-stroke">記事管理</h2>
            <div className="d-flex gap-2">
              <Button
                variant="success"
                onClick={() => navigate('/admin/articles/new')}
              >
                新規記事作成
              </Button>
              <AdminMenuButton onToggle={() => {}} isOpen={false} />
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* 検索・フィルター */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">検索・フィルター</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>キーワード検索</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="記事のタイトルや内容を検索..."
                        value={searchKeyword}
                        onChange={e => setSearchKeyword(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleSearch()}
                      />
                      <Button variant="outline-primary" onClick={handleSearch}>
                        検索
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>並び順</Form.Label>
                    <Form.Select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={e => {
                        const [newSortBy, newSortOrder] = e.target.value.split(
                          '-'
                        ) as [
                          ArticleSearchParams['sortBy'],
                          ArticleSearchParams['sortOrder'],
                        ];
                        handleSortChange(newSortBy, newSortOrder);
                      }}
                    >
                      <option value="created_at-desc">作成日 (新しい順)</option>
                      <option value="created_at-asc">作成日 (古い順)</option>
                      <option value="updated_at-desc">更新日 (新しい順)</option>
                      <option value="updated_at-asc">更新日 (古い順)</option>
                      <option value="title-asc">タイトル (A-Z)</option>
                      <option value="title-desc">タイトル (Z-A)</option>
                      <option value="view_count-desc">閲覧数 (多い順)</option>
                      <option value="view_count-asc">閲覧数 (少ない順)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <div>
                      <Button
                        variant="outline-secondary"
                        onClick={clearFilters}
                      >
                        フィルターをクリア
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>カテゴリフィルター</Form.Label>
                    <div>
                      {categories.map(category => (
                        <Badge
                          key={category.id}
                          bg={
                            selectedCategories.includes(category.name)
                              ? 'primary'
                              : 'outline-primary'
                          }
                          className="me-1 mb-1"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleCategoryFilter(category.name)}
                        >
                          {category.name}
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>タグフィルター</Form.Label>
                    <div>
                      {tags.map(tag => (
                        <Badge
                          key={tag.id}
                          bg={
                            selectedTags.includes(tag.name)
                              ? 'success'
                              : 'outline-success'
                          }
                          className="me-1 mb-1"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleTagFilter(tag.name)}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* 記事統計 */}
          <div className="mb-4">
            <p className="text-muted">
              {totalArticles > 0 ? (
                <>
                  {startItem}-{endItem} / {totalArticles}件の記事を表示中
                  {(selectedCategories.length > 0 ||
                    selectedTags.length > 0 ||
                    searchKeyword) &&
                    ' (フィルター適用中)'}
                </>
              ) : (
                '記事が見つかりませんでした'
              )}
            </p>
          </div>

          {/* 記事一覧 */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner
                animation="border"
                role="status"
                className="text-success"
              >
                <span className="visually-hidden">読み込み中...</span>
              </Spinner>
              <div className="mt-3">記事を読み込み中...</div>
            </div>
          ) : (
            <Row>
              {articles.map(article => (
                <Col key={article.id} xs={12} className="mb-4">
                  <Card>
                    <Row className="g-0">
                      {article.featuredImage && (
                        <Col md={4}>
                          <img
                            src={getImageUrl(article.featuredImage) || ''}
                            alt={article.title}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                            }}
                          />
                        </Col>
                      )}
                      <Col md={article.featuredImage ? 8 : 12}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h5
                                className="card-title"
                                style={{ cursor: 'pointer', color: '#0d6efd' }}
                                onClick={() => handleArticleClick(article)}
                              >
                                {article.title}
                              </h5>

                              {article.summary && (
                                <p className="card-text text-muted">
                                  {stripHtml(article.summary)}
                                </p>
                              )}

                              <div className="mb-2">
                                {article.categories.map(category => (
                                  <Badge
                                    key={category}
                                    bg="primary"
                                    className="me-1"
                                  >
                                    {category}
                                  </Badge>
                                ))}
                                {article.tags.map(tag => (
                                  <Badge
                                    key={tag}
                                    bg="success"
                                    className="me-1"
                                  >
                                    #{tag}
                                  </Badge>
                                ))}
                              </div>

                              <small className="text-muted">
                                作成: {formatDate(article.createdAt)} | 更新:{' '}
                                {formatDate(article.updatedAt)} | 作成者:{' '}
                                {article.authorName}
                                {article.viewCount !== undefined &&
                                  ` | 閲覧数: ${article.viewCount}`}
                              </small>
                            </div>

                            <div className="ms-3">
                              <div className="d-flex flex-column gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/admin/articles/${article.id}/edit`
                                    )
                                  }
                                >
                                  編集
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteArticle(article.id)
                                  }
                                >
                                  削除
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* ページネーション */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />

                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNumber: number;
                  if (totalPages <= 5) {
                    pageNumber = index + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage > totalPages - 3) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }

                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === currentPage}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}

                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          )}
        </Col>
      </Row>

      {/* 記事詳細モーダル */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedArticle?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedArticle && (
            <>
              {selectedArticle.featuredImage && (
                <img
                  src={getImageUrl(selectedArticle.featuredImage) || ''}
                  alt={selectedArticle.title}
                  className="img-fluid mb-3 rounded"
                  style={{
                    maxHeight: '300px',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}

              <div className="mb-3">
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {selectedArticle.categories.map((category, index) => (
                    <Badge key={index} bg="primary">
                      {category}
                    </Badge>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-1 mb-3">
                  {selectedArticle.tags.map((tag, index) => (
                    <Badge key={index} bg="secondary">
                      #{tag}
                    </Badge>
                  ))}
                </div>
                <small className="text-muted">
                  投稿日: {formatDate(selectedArticle.createdAt)} | 作成者:{' '}
                  {selectedArticle.authorName}
                  {selectedArticle.viewCount && (
                    <> | 閲覧数: {selectedArticle.viewCount}</>
                  )}
                </small>
              </div>

              {selectedArticle.summary && (
                <div className="mb-3 p-3 bg-light rounded">
                  <h6>概要</h6>
                  <p className="mb-0">{stripHtml(selectedArticle.summary)}</p>
                </div>
              )}

              <div
                className="article-content"
                dangerouslySetInnerHTML={{
                  __html: stripHtml(selectedArticle.content),
                }}
                style={{
                  lineHeight: '1.6',
                  fontSize: '1rem',
                }}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Articles;
